import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  TrendingUp,
  FileCheck,
  Target,
  BarChart3,
  Scale,
  Users,
  Stethoscope,
  CheckCircle2,
} from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      id: 'revenue-cycle',
      icon: BarChart3,
      title: 'Revenue Cycle Optimization',
      description: 'End-to-end revenue cycle assessment and process improvement to accelerate cash flow and reduce leakage.',
      deliverables: [
        'Revenue cycle performance benchmarking',
        'Workflow analysis and redesign',
        'Denial management optimization',
        'A/R reduction strategies',
        'KPI development and monitoring',
      ],
      impact: 'Optimized revenue cycles reduce days in A/R by 5-15 days and improve net collection rates by 2-5%.',
    },
    {
      id: 'training',
      icon: Users,
      title: 'Coder & CDI Training and Education',
      description: 'Comprehensive training programs designed to elevate coder proficiency and CDI specialist effectiveness.',
      deliverables: [
        'Customized coding education curricula',
        'CDI specialist onboarding and advancement training',
        'Specialty-specific coding workshops',
        'Regulatory update training sessions',
        'Competency assessments and certification preparation',
      ],
      impact: 'Organizations investing in structured training see 20-30% improvement in coding accuracy and reduced staff turnover.',
    },
    {
      id: 'drg',
      icon: Target,
      title: 'DRG Validation',
      description: 'Comprehensive DRG accuracy reviews to maximize appropriate reimbursement and minimize compliance risk.',
      deliverables: [
        'Pre-bill and retrospective DRG reviews',
        'Coding accuracy assessment',
        'Overpayment and underpayment identification',
        'Coder education and feedback',
      ],
      impact: 'Organizations implementing rigorous DRG validation recover an average of $500K-$2M annually in missed revenue.',
    },
    {
      id: 'documentation-integrity',
      icon: FileCheck,
      title: 'Documentation Integrity (CDI Reviews)',
      description: 'Documentation improvement programs for CDI specialists and providers to capture accurate clinical complexity and support appropriate reimbursement.',
      deliverables: [
        'CDI program assessment and design',
        'CDI reviews',
        'Provider education and engagement',
        'Query optimization and workflow analysis',
        'Documentation audit and feedback',
        'Severity of illness / risk of mortality improvement',
      ],
      impact: 'Effective documentation integrity programs improve case mix index by 0.05-0.15 points, translating to significant revenue per discharge.',
    },
    {
      id: 'coding-denial',
      icon: Scale,
      title: 'Coding & CDI Denial Assistance',
      description: 'Expert support for coding-related denials and CDI query resolution to maximize revenue recovery.',
      deliverables: [
        'Denial root cause analysis and trending',
        'Clinical validation denial appeals',
        'Medical necessity denial management',
        'CDI query optimization and response support',
        'Coder and CDI specialist education',
      ],
      impact: 'Organizations leveraging specialized denial assistance recover 60-80% of appealed claims and reduce future denial rates by 25-35%.',
    },
    {
      id: 'physician-collaboration',
      icon: Stethoscope,
      title: 'Physician to Physician Collaboration',
      description: 'Clinical peer engagement to bridge documentation gaps and enhance provider understanding of reimbursement impact.',
      deliverables: [
        'Physician advisor consultations',
        'Clinical documentation peer reviews',
        'Provider-to-provider education sessions',
        'Query response optimization with clinical context',
        'Medical staff engagement strategies',
      ],
      impact: 'Physician-led collaboration improves query response rates by 40-60% and builds sustainable documentation culture.',
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="hero-texture bg-[#0F172A] relative">
        <div className="container-custom relative z-10 py-24 sm:py-32">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#0D9488] mb-4">
              Our Services
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-white font-['Outfit']">
              Comprehensive Revenue Cycle Solutions
            </h1>
            <p className="mt-6 text-xl text-gray-300 leading-relaxed">
              From payer strategy to CDI, DRG validation to denial management—we deliver the expertise healthcare organizations need to optimize reimbursement and strengthen documentation integrity.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding bg-[#FAFAFA]" data-testid="services-list">
        <div className="container-custom">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div
                key={service.id}
                id={service.id}
                className="bg-white border border-[#E2E8F0] p-8 sm:p-12 scroll-mt-24"
                data-testid={`service-${service.id}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                  {/* Service Header */}
                  <div className="lg:col-span-1">
                    <div className="icon-container mb-6 w-16 h-16">
                      <service.icon className="h-8 w-8" />
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-semibold text-[#0F172A] font-['Outfit'] mb-4">
                      {service.title}
                    </h2>
                    <p className="text-[#64748B] leading-relaxed">
                      {service.description}
                    </p>
                  </div>

                  {/* Deliverables */}
                  <div className="lg:col-span-1">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-[#0D9488] mb-4">
                      Key Deliverables
                    </h3>
                    <ul className="space-y-3">
                      {service.deliverables.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-[#0D9488] flex-shrink-0 mt-0.5" />
                          <span className="text-[#64748B]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Business Impact */}
                  <div className="lg:col-span-1">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-[#0D9488] mb-4">
                      Business Impact
                    </h3>
                    <div className="bg-[#FAFAFA] border-l-4 border-[#0D9488] p-6">
                      <p className="text-[#0F172A] leading-relaxed">
                        {service.impact}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="section-padding bg-white" data-testid="process-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#0D9488] mb-4">
              Our Process
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-[#0F172A] font-['Outfit']">
              How We Engage
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'Comprehensive assessment of your current state, challenges, and objectives.' },
              { step: '02', title: 'Analysis', desc: 'Deep-dive into data, processes, and payer dynamics to identify opportunities.' },
              { step: '03', title: 'Strategy', desc: 'Development of tailored recommendations with clear implementation roadmap.' },
              { step: '04', title: 'Execution', desc: 'Hands-on support to implement solutions and measure outcomes.' },
            ].map((item, index) => (
              <div key={index} className="text-center" data-testid={`process-step-${index}`}>
                <div className="text-5xl font-light text-[#0D9488] font-['Outfit'] mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-[#0F172A] font-['Outfit'] mb-3">
                  {item.title}
                </h3>
                <p className="text-[#64748B]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0F172A] py-20">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-white font-['Outfit'] mb-6">
            Let's Discuss Your Specific Needs
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
            Every organization's reimbursement challenges are unique. Schedule a consultation to explore how we can help.
          </p>
          <Link to="/contact" data-testid="services-cta">
            <Button className="bg-[#0D9488] hover:bg-[#0f766e] text-white rounded-md px-10 py-6 text-lg group">
              Schedule a Consultation
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
